import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Avatar } from '@/components/brand/Brand';
import { SearchIcon } from '@/components/icons';
import { Button } from '@/components/ui/Button';
import { EmptyState, Skeleton } from '@/components/ui/Feedback';
import { SectionBar } from '@/components/ui/SectionBar';
import { useToast } from '@/components/ui/Toast';
import { socialApi } from '@/lib/api/endpoints';
import { qk, useUserSearch } from '@/lib/api/queries';
import type { UserSearchResult } from '@/lib/api/types';
import { describeHandicap } from './FriendsList';
import { t } from '@/lib/i18n';
import styles from './Friends.module.css';

/**
 * A busca de jogadores, sem o cromo da tela em volta.
 *
 * Existe separada porque o desktop a abre em modal (canvas 1u: "tarefa curta e não merece
 * página") enquanto o celular continua com a página inteira (1q). Uma implementação só: se a
 * regra de busca mudar, ela muda nos dois lugares por construção.
 */
export function FriendSearch({ autoFocus = false }: { autoFocus?: boolean }) {
  const [query, setQuery] = useState('');
  const results = useUserSearch(query);
  const toast = useToast();
  const queryClient = useQueryClient();
  const [sending, setSending] = useState<string | null>(null);
  const [justSent, setJustSent] = useState<Set<string>>(new Set());

  const trimmed = query.trim();

  const send = async (user: UserSearchResult) => {
    setSending(user.id);
    try {
      await socialApi.sendRequest(user.id);
      // Marca localmente antes de revalidar: a linha muda para "Pedido enviado" no toque,
      // sem esperar a busca voltar do servidor.
      setJustSent((prev) => new Set(prev).add(user.id));
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: qk.friendRequests }),
        queryClient.invalidateQueries({ queryKey: ['users', 'search'] }),
      ]);
      toast.success(t('search.sent', { name: user.displayName }));
    } catch (e) {
      toast.error(e instanceof Error ? e.message : t('search.sendFailed'));
    } finally {
      setSending(null);
    }
  };

  return (
    <>
      <div className={styles.searchWrap}>
        <SearchIcon size={18} className={styles.searchIcon} />
        <input
          className={styles.search}
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={t('search.placeholder')}
          aria-label={t('search.aria')}
          autoComplete="off"
          autoFocus={autoFocus}
        />
      </div>

      {trimmed.length > 0 && (
        <>
          <SectionBar>{t('search.results')}</SectionBar>
          {results.isLoading ? (
            <ResultsSkeleton />
          ) : (results.data?.length ?? 0) === 0 ? (
            <EmptyState title={t('search.empty.title')}>{t('search.empty.body')}</EmptyState>
          ) : (
            results.data?.map((user) => (
              <div key={user.id} className={styles.row}>
                <Avatar name={user.displayName} size={40} variant="green" />
                <div className={styles.rowBody}>
                  <div className={styles.rowName}>{user.displayName}</div>
                  <div className={styles.rowMeta}>{describeResult(user)}</div>
                </div>
                <ResultAction
                  user={user}
                  sent={justSent.has(user.id)}
                  busy={sending === user.id}
                  onSend={() => void send(user)}
                />
              </div>
            ))
          )}
        </>
      )}
    </>
  );
}

function ResultAction({
  user,
  sent,
  busy,
  onSend,
}: {
  user: UserSearchResult;
  sent: boolean;
  busy: boolean;
  onSend: () => void;
}) {
  if (user.relation === 'friend') {
    return <span className={styles.sent}>{t('search.alreadyFriend')}</span>;
  }
  if (user.relation === 'request_sent' || sent) {
    return <span className={styles.sent}>{t('search.requestSent')}</span>;
  }
  if (user.relation === 'request_received') {
    // Quem já convidou você não precisa de um segundo pedido — a decisão está na outra tela.
    return <span className={styles.sent}>{t('search.invitedYou')}</span>;
  }
  return (
    <Button size="sm" busy={busy} onClick={onSend}>
      {t('sidebar.addFriend')}
    </Button>
  );
}

/** "Handicap 16,8 · Guarapiranga · 2 amigos em comum" — omitindo o que não existe. */
function describeResult(user: UserSearchResult): string {
  const parts = [describeHandicap(user.handicapIndex)];
  if (user.homeCourseName) parts.push(user.homeCourseName);
  if (user.mutualFriends > 0) {
    parts.push(t('search.mutualFriends', { count: user.mutualFriends }));
  }
  return parts.join(' · ');
}

function ResultsSkeleton() {
  return (
    <>
      {[0, 1].map((i) => (
        <div key={i} className={styles.row}>
          <Skeleton width={40} height={40} radius="999px" />
          <div className={styles.rowBody}>
            <Skeleton width="50%" height={14} />
            <Skeleton width="70%" height={10} style={{ marginTop: 6 }} />
          </div>
        </div>
      ))}
    </>
  );
}
